import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import PriceChart from '../../components/charts/PriceChart';
import { getPriceCheck, searchCards } from '../../api/dashboard';

export default function PriceChecker({ initialCardId }: { initialCardId?: string | null }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef<string | null>(null);

  useEffect(() => {
    if (initialCardId && initialCardId !== loadedRef.current) {
      loadedRef.current = initialCardId;
      setQuery(initialCardId);
      setLoading(true);
      setSuggestions([]);
      getPriceCheck(initialCardId)
        .then(d => setData(d))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [initialCardId]);

  const handleSearch = async (q?: string) => {
    const val = q || query;
    if (!val.trim()) return;

    // If it looks like a card_id (contains dash)
    if (val.includes('-')) {
      setLoading(true);
      setSuggestions([]);
      try {
        const d = await getPriceCheck(val.trim());
        setData(d);
      } catch { }
      setLoading(false);
    } else {
      // Search by name
      try {
        const results = await searchCards(val);
        if (results?.direct?.card) {
          // Direct match from grading-checker
          setLoading(true);
          const d = await getPriceCheck(results.direct.card.id);
          setData(d);
          setSuggestions([]);
          setLoading(false);
        } else if (results?.cards?.length === 1) {
          setLoading(true);
          const d = await getPriceCheck(results.cards[0].id);
          setData(d);
          setSuggestions([]);
          setLoading(false);
        } else {
          setSuggestions(results?.cards || []);
          setData(null);
        }
      } catch { }
    }
  };

  const selectCard = async (cardId: string) => {
    setLoading(true);
    setSuggestions([]);
    setQuery(cardId);
    try {
      const d = await getPriceCheck(cardId);
      setData(d);
    } catch { }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Price Checker</h2>
        <p className="text-sm text-neutral-500 mt-1">Prix multi-sources pour une carte</p>
      </div>

      {/* Search */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-3 text-neutral-500" />
          <input
            type="text" value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Card ID (sv08-238) ou nom (pikachu, charizard...)"
            className="w-full pl-10 pr-4 py-2.5 bg-surface border border-border rounded-xl text-white placeholder:text-neutral-600 focus:outline-none focus:border-accent"
          />
        </div>
        <button onClick={() => handleSearch()} className="px-6 py-2.5 bg-accent rounded-xl text-white font-medium hover:bg-accent/80">
          Rechercher
        </button>
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          {suggestions.slice(0, 10).map(s => (
            <div key={s.id} onClick={() => selectCard(s.id)}
              className="px-4 py-3 hover:bg-neutral-800 cursor-pointer border-b border-border/50 flex justify-between">
              <span className="text-white">{s.name_fr || s.name}</span>
              <span className="text-neutral-500 text-sm">{s.id} — {s.set_id}</span>
            </div>
          ))}
        </div>
      )}

      {loading && <div className="text-center py-10 text-neutral-500">Chargement...</div>}

      {/* Results */}
      {data?.card && (
        <div className="space-y-6">
          {/* Card header */}
          <div className="bg-surface border border-border rounded-2xl p-6 flex gap-6">
            <img src={`${process.env.REACT_APP_API_URL || ''}/img/opus-assets/cards/${data.card.id}.png`} alt="" className="w-32 h-44 rounded-lg flex-shrink-0 object-cover bg-neutral-700" />
            <div className="flex-1">
              <h3 className="text-xl font-bold text-white">{data.card.name_fr || data.card.name}</h3>
              <p className="text-sm text-neutral-500 mt-1">{data.card.id} — {data.card.set_id} — {data.card.rarity}</p>

              {/* Sources grid */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                {data.sources.cardmarket && (
                  <div className="bg-dark rounded-xl p-3">
                    <div className="text-xs text-neutral-500">Cardmarket EU</div>
                    <div className="text-lg font-bold text-accent">{Number(data.sources.cardmarket.trend).toFixed(2)}€</div>
                    <div className="text-xs text-neutral-500">low: {Number(data.sources.cardmarket.lowest).toFixed(2)}€</div>
                  </div>
                )}
                {data.sources.tcggo && (
                  <div className="bg-dark rounded-xl p-3">
                    <div className="text-xs text-neutral-500">CM France</div>
                    <div className="text-lg font-bold text-green-400">{data.sources.tcggo.fr ? `${Number(data.sources.tcggo.fr).toFixed(2)}€` : '—'}</div>
                    <div className="text-xs text-neutral-500">EU: {data.sources.tcggo.eu ? `${Number(data.sources.tcggo.eu).toFixed(2)}€` : '—'}</div>
                  </div>
                )}
                {data.sources.pokeinvest && (
                  <div className="bg-dark rounded-xl p-3">
                    <div className="text-xs text-neutral-500">eBay USD</div>
                    <div className="text-lg font-bold text-yellow-400">${Number(data.sources.pokeinvest.price).toFixed(2)}</div>
                  </div>
                )}
              </div>

              {/* Country prices */}
              {data.sources.tcggo && (
                <div className="flex gap-4 mt-3 text-sm">
                  {['de', 'es', 'it'].map(c => (
                    data.sources.tcggo[c] ? (
                      <span key={c} className="text-neutral-400">
                        {c.toUpperCase()}: {Number(data.sources.tcggo[c]).toFixed(2)}€
                      </span>
                    ) : null
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Price chart */}
          {data.history?.length > 0 && (
            <div>
              <h3 className="text-sm text-neutral-500 uppercase tracking-wider mb-3">Evolution du prix (30j)</h3>
              <PriceChart
                data={data.history.map((h: any) => ({ date: h.date, price: Number(h.trend) }))}
              />
            </div>
          )}

          {/* All sources detail */}
          {data.sources.collectr && (
            <div>
              <h3 className="text-sm text-neutral-500 uppercase tracking-wider mb-3">Prix grades (Collectr)</h3>
              <div className="grid grid-cols-4 gap-2">
                {data.sources.collectr.slice(0, 12).map((c: any, i: number) => (
                  <div key={i} className="bg-surface border border-border rounded-lg p-3 text-center">
                    <div className="text-xs text-neutral-500">{c.product_sub_type}</div>
                    <div className="text-sm font-bold text-white">${Number(c.market_price).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
